import { Tabs, Tab } from '@heroui/react';
import { twMerge } from 'tailwind-merge';
import { type InferKeyOrTitle, type Segment, type SegmentedControlProps } from './SegmentedControl.types';

/**
 * If your segment objects have a `key` property, that is what will be passed to the `onSegmentChange` callback.
 * Otherwise, the `title` property will be passed.
 * The same logic applies to the `selectedSegment` prop. If your segments have keys selectedSegment should be one of those keys
 * otherwise it should be the `title` of the segment.
 */
export const SegmentedControl = <Segments extends readonly Segment[]>({
    segments,
    classNames,
    onSegmentChange,
    selectedSegment,
}: SegmentedControlProps<Segments>) => (
    <Tabs
        variant="underlined"
        color="primary"
        fullWidth
        selectedKey={selectedSegment as string}
        onSelectionChange={key => {
            onSegmentChange?.(key as InferKeyOrTitle<Segments>);
        }}
        classNames={{
            wrapper: twMerge([classNames?.wrapper]),
            base: twMerge(['overflow-x-hidden', classNames?.base]),
            tabList: twMerge([
                'p-0',
                'border-b border-solid border-b-slate-grey-200',
                'overflow-x-visible',
                classNames?.tabList,
            ]),
            tab: twMerge(['data-[hover-unselected=true]:opacity-100', 'pb-5 h-9', classNames?.tab]),
            tabContent: twMerge(['text-base', 'text-black', 'font-semibold', classNames?.tabContent]),
            cursor: twMerge(['w-full', 'bottom-[-1px]', 'rounded-1', 'shadow-none', classNames?.cursor]),
            panel: twMerge(['p-0', classNames?.panel]),
        }}
    >
        {segments.map(({ title, content, disabled, ...rest }) => (
            <Tab key={'key' in rest ? rest.key : title} title={title} disabled={disabled}>
                {content}
            </Tab>
        ))}
    </Tabs>
);
